import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { takeUntil, tap } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';

@Component({
  selector: 'formly-field-name',
  template: `
    <div class="mb-4">
      <h3 class="mb-2">
        {{ props.label }}
        <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
      </h3>
      <div class="dd-name-fields">
        @for (field of field.fieldGroup; track $index) {
          <formly-field [field]="field" [class]="field.className"></formly-field>
        }
      </div>
    </div>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
    }
    <app-formly-error [field]="field"></app-formly-error>
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldName extends FieldType implements OnInit {
  private destroy$ = injectDestroy();
  constructor() {
    super();
  }

  ngOnInit() {
    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          // No "both parts filled" guard: createFullName() skips empty parts, so clearing a name
          // now clears fullName too instead of leaving a stale composite behind. The equality
          // check below stops the patch from re-triggering valueChanges endlessly.
          const fullName = this.createFullName();

          if (fullName != this.field.formControl.get('fullName').value) {
            this.field.formControl.get('fullName').patchValue(fullName, { emitEvent: true });
          }
        })
      )
      .subscribe();

    this.updateFullName();
  }

  /**
   * Composite display name from the filled parts only. An unfilled name must yield an empty
   * string — interpolating the raw controls wrote a literal "undefined undefined" into fullName
   * on init, which then surfaced verbatim wherever the composite is rendered (e.g. the wizard
   * review summary).
   */
  createFullName() {
    const part = (key: string): string => {
      const value = this.field.formControl.get(key)?.value;
      return typeof value === 'string' ? value.trim() : '';
    };
    return [part('firstName'), part('affix'), part('lastName')].filter(Boolean).join(' ');
  }

  updateFullName() {
    const fullName = this.createFullName();
    this.field.formControl.get('fullName').patchValue(fullName, { emitEvent: true });
  }
}
